"use client";

import React, { useCallback } from "react";
import { type Editor } from "@tiptap/react";
import { Toggle } from "@os/components/ui/toggle";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@os/components/ui/select";
import {
  Bold,
  Strikethrough,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Link,
  Image as ImageIcon,
  Loader2,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
} from "lucide-react";

type Props = {
  editor: Editor | null;
  onAddImage: () => void;
  isUploadingImage?: boolean;
};

const SlideFormattingBar: React.FC<Props> = ({ editor, onAddImage, isUploadingImage = false }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  // Font options (same as MapleWord)
  const fontFamilies = [
    { label: "Default", value: "default" },
    { label: "Inter", value: "Inter, system-ui, sans-serif" },
    { label: "Arial", value: "Arial, Helvetica, sans-serif" },
    { label: "Times New Roman", value: "\"Times New Roman\", Times, serif" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Courier New", value: "\"Courier New\", Courier, monospace" },
    { label: "JetBrains Mono", value: "\"JetBrains Mono\", monospace" },
  ];
  const fontSizes = [
    { label: "12", value: "12px" },
    { label: "14", value: "14px" },
    { label: "16", value: "16px" },
    { label: "18", value: "18px" },
    { label: "20", value: "20px" },
    { label: "24", value: "24px" },
    { label: "28", value: "28px" },
  ];

  const applyFontFamily = (value: string) => {
    if (!editor) return;
    if (value === "default") {
      editor.chain().focus().unsetFontFamily().run();
    } else {
      editor.chain().focus().setFontFamily(value).run();
    }
  };

  const applyFontSize = (value: string) => {
    if (!editor) return;
    editor.chain().focus().setFontSize(value).run();
  };

  const applyTextColor = (value: string) => {
    if (!editor) return;
    editor.chain().focus().setColor(value).run();
  };

  const applyHighlight = (value: string) => {
    if (!editor) return;
    editor.chain().focus().setHighlight({ color: value }).run();
  };

  if (!editor) return null;

  return (
    <div className="border border-input bg-background rounded-md p-2 mb-3 flex flex-wrap items-center gap-2">
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 1 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 2 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("heading", { level: 3 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("strike")} onPressedChange={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("bulletList")} onPressedChange={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("orderedList")} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("codeBlock")} onPressedChange={() => editor.chain().focus().toggleCodeBlock().run()}>
        <Code className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("subscript")} onPressedChange={() => editor.chain().focus().toggleSubscript().run()}>
        <SubscriptIcon className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("superscript")} onPressedChange={() => editor.chain().focus().toggleSuperscript().run()}>
        <SuperscriptIcon className="h-4 w-4" />
      </Toggle>
      <Toggle size="sm" onPressedChange={onAddImage} disabled={isUploadingImage}>
        {isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("link")} onPressedChange={setLink}>
        <Link className="h-4 w-4" />
      </Toggle>

      <span className="h-6 w-px bg-border mx-1" />

      <div className="flex items-center gap-1">
        <Select onValueChange={applyFontFamily}>
          <SelectTrigger className="h-8 w-36">
            <SelectValue placeholder="Font" />
          </SelectTrigger>
          <SelectContent>
            {fontFamilies.map((f) => (
              <SelectItem key={f.label} value={f.value}>
                <span style={{ fontFamily: f.value || undefined }}>{f.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1">
        <Select onValueChange={applyFontSize}>
          <SelectTrigger className="h-8 w-24">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            {fontSizes.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1">
        <input
          type="color"
          aria-label="Text color"
          className="h-8 w-8 rounded-md border border-input bg-background"
          onChange={(e) => applyTextColor(e.target.value)}
        />
      </div>

      <div className="flex items-center gap-1">
        <input
          type="color"
          aria-label="Highlight color"
          className="h-8 w-8 rounded-md border border-input bg-background"
          onChange={(e) => applyHighlight(e.target.value)}
        />
      </div>
    </div>
  );
};

export default SlideFormattingBar;